/**
 * Color Picker Component
 * 
 * Reusable color picker with preset palette and custom color selection
 */

import React from 'react';
import { Box, TextField } from '@mui/material';

interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  placeholder?: string;
}

// Predefined color palette
const COLOR_PALETTE = [
  '#000000', '#ffffff', '#e0e0e0', '#9e9e9e', '#616161',
  '#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5',
  '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
  '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800',
  '#ff5722', '#795548', '#607d8b'
];

export const ColorPicker: React.FC<ColorPickerProps> = ({
  label,
  value,
  onChange,
  placeholder = 'transparent or #ffffff'
}) => {
  // Helper to check if value is transparent
  const isTransparent = !value || value === 'transparent' || value === 'rgba(0,0,0,0)' || value === 'rgba(0, 0, 0, 0)';

  // Get actual color for native color picker (can't handle transparent)
  // Use #000001 (visually black but distinguishable from transparent) so we can detect when user selects black
  const colorPickerValue = isTransparent ? '#000001' : value;

  return (
    <Box>
      {/* Color input with preview */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          label={label}
          value={isTransparent ? 'transparent' : value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          InputProps={{
            startAdornment: (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  mr: 1,
                  // Show checkerboard pattern for transparent colors
                  ...(isTransparent ? {
                    backgroundColor: '#fff',
                    backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)',
                    backgroundSize: '8px 8px',
                    backgroundPosition: '0 0, 4px 4px'
                  } : {
                    backgroundColor: value,
                    backgroundImage: 'none'
                  })
                }}
              />
            )
          }}
        />
        {!isTransparent && (
          <input
            type="color"
            value={colorPickerValue}
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: 40,
              height: 40,
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              padding: 0
            }}
            title="Pick custom color"
          />
        )}
      </Box>

      {/* Quick color palette */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 0.5,
        p: 1,
        bgcolor: 'grey.50',
        borderRadius: 1
      }}>
        {/* Transparent option */}
        <Box
          onClick={() => onChange('transparent')}
          sx={{
            width: 24,
            height: 24,
            borderRadius: 0.5,
            border: 1,
            borderColor: 'divider',
            cursor: 'pointer',
            backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)',
            backgroundSize: '8px 8px',
            backgroundPosition: '0 0, 4px 4px',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: 1
            },
            transition: 'all 0.2s'
          }}
          title="Transparent"
        />
        
        {/* Color palette */}
        {COLOR_PALETTE.map(color => (
          <Box
            key={color}
            onClick={() => onChange(color)}
            sx={{
              width: 24,
              height: 24,
              borderRadius: 0.5,
              backgroundColor: color,
              border: 1,
              borderColor: value === color ? 'primary.main' : 'divider',
              borderWidth: value === color ? 2 : 1,
              cursor: 'pointer',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: 1
              },
              transition: 'all 0.2s'
            }}
            title={color}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ColorPicker;